import React, {ReactElement, useEffect, useState} from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import Review, {ReviewChecklistItem, ReviewItem, ReviewStatus} from "../../model/Review";
import Model from "../../model/Model";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import DialogButton from "../../components/form/DialogButton";
import globalStyles from "../../styles/globalStyles";
import {UpdateReviewInput} from "../../API";
import {useResources} from "../../stores/ResourceProvider";
import {getISODateToday, humanizeString, isoToLocalDateString} from "../../stores/StoreUtilities";
import {ReviewStep} from "./ReviewDialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const ReviewChecklist = ({
  review,
  model,
  step,
  isDialog,
  updatedAt,
  onChange,
  onChangeView,
  onSubmit,
  onClose,
  onDecline,
} : {
  review: Review
  model: Model
  step: ReviewStep
  isDialog?: boolean
  updatedAt: string
  onChange?(review: Review): void
  onChangeView?(view: string): void
  onSubmit?(review: Review): void
  onClose?(): void
  onDecline?(review: Review): void
}) => {
  const [isManaged, setIsManaged] = useState<boolean>(false)
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>(ReviewStatus.Pending)
  const questionaireUrl = "https://forms.gle/g8HmTJiCZ4AiFaLK6" // 2026
  const portalVaultUrl = "https://wealth.emaplan.com/ema/ria/parkerfinancialllc"

  const {userStore, notify} = useResources()

  useEffect(() => {
    if (review) {
      setIsManaged(review.managed)
      setReviewStatus(review.status ?? ReviewStatus.Pending)
    }
  }, [review, updatedAt])

  const theme = useTheme()
  const styles = {
    itemBox: {
      display:"flex",
      flexGrow:1,
      alignItems:"center",
      width: "100%",
      maxHeight:40,
      backgroundColor: theme.palette.grey["50"],
      borderWidth: 1,
      borderColor: theme.palette.background.default,
      borderStyle: "solid",
      borderRadius: 0,
      padding: 0,
      marginBottom: 1
    },
    link: {
      fontWeight: 700,
      textDecoration: "none"
    }
  }

  const handleCheckItem = async (item: ReviewChecklistItem, index: number, checked: boolean) => {
    if (review.checklist) {
      review.checklist[index].completed = checked
      const input: UpdateReviewInput = {
        id: review.id,
        checklist: JSON.stringify(review.checklist),
        status: ReviewStatus.InProgress
      }

      const update = await userStore.updateReview(input)
      if (update && onChange) {
        onChange(update)
      }
    }
  }

  // const handleReviewStatusChange = async (status: ReviewStatus)=> {
  //   const input: UpdateReviewInput = {
  //     id: review.id,
  //     status: status
  //   }
  //
  //   const update = await userStore.updateReview(input)
  //   if (update && onChange) {
  //     setReviewStatus(status)
  //     onChange(update)
  //   }
  // }

  const renderChecklistItem = (item: ReviewChecklistItem, index: number) => {
    let content: ReactElement
    let button: ReactElement | null = null

    if (!isManaged && (item.reviewItem === ReviewItem.Questionaire || item.reviewItem === ReviewItem.TaxReturn)) {
      return null
    }

    switch (item.reviewItem) {
      case ReviewItem.Questionaire:
        content =
          <Typography variant="body1"><MuiLink href={questionaireUrl} target="_blank" sx={styles.link}>Annual Review Questionnaire:</MuiLink>&nbsp;
              Please complete our <MuiLink href={questionaireUrl} target="_blank" sx={styles.link}>annual review questionnaire</MuiLink>. Your answers will help us focus our time together.
          </Typography>
        button =
          <IconButton size="medium" onClick={() => window.open(questionaireUrl, "_blank")}>
            <ArrowCircleRightIcon sx={{height:32, width:32}}/>
          </IconButton>
        break
      case ReviewItem.TaxReturn:
        content =
          <Typography variant="body1"><MuiLink href={portalVaultUrl} target="_blank" sx={styles.link}>Current Tax Return:</MuiLink>&nbsp;
            Please either email your most recent tax return (all pages) to us or <MuiLink href={portalVaultUrl} target="_blank" sx={styles.link}>click here</MuiLink> to upload it to your Parker Financial Portal vault into the Shared Folder and then into the Client Originals folder. Uploading it is preferred as it is more secure than email.
        </Typography>
        button =
          <IconButton size="medium" onClick={() => window.open(portalVaultUrl, "_blank")}>
            <ArrowCircleRightIcon sx={{height:32, width:32}}/>
          </IconButton>
        break
      case ReviewItem.Timeline:
        content =
          <Typography variant="body1"><MuiLink href="#" onClick={() => handleChangeView(ReviewStep.Timeline)} sx={styles.link}>Timeline:</MuiLink>&nbsp;
            Please update the people and timeline for your retirement.
          </Typography>
        button =
          <IconButton size="medium" onClick={() => handleChangeView(ReviewStep.Timeline)}>
            <ArrowCircleRightIcon sx={{height:32, width:32}}/>
          </IconButton>
        break
      case ReviewItem.AssetsAndLiabilities:
        content = isManaged ?
          <Typography variant="body1"><MuiLink href="#" onClick={() => handleChangeView(ReviewStep.Networth)} sx={styles.link}>Assets & Liabilities:</MuiLink>&nbsp;
            Please provide us with the most current balances for any HELD AWAY assets and outstanding liabilities.
          </Typography> :
          <Typography variant="body1"><MuiLink href="#" onClick={() => handleChangeView(ReviewStep.Networth)} sx={styles.link}>Assets & Liabilities:</MuiLink>&nbsp;
            Please provide us with the most current balances for all assets and outstanding liabilities.
          </Typography>
        button =
          <IconButton size="medium" onClick={() => handleChangeView(ReviewStep.Networth)}>
            <ArrowCircleRightIcon sx={{height:32, width:32}}/>
          </IconButton>
        break
      case ReviewItem.Budget:
        content =
          <Typography variant="body1"><MuiLink href="#" onClick={() => handleChangeView(ReviewStep.Budget)} sx={styles.link}>Budget:</MuiLink>&nbsp;
            Please provide us with the most current income sources, deductions, tax withholdings, and expenses.
          </Typography>
        button =
          <IconButton size="medium" onClick={() => handleChangeView(ReviewStep.Budget)}>
            <ArrowCircleRightIcon sx={{height:32, width:32}}/>
          </IconButton>
        break
      default:
        return null
    }

    return (
      <TableRow>
        <TableCell align="center">
          <Checkbox name="incomeLock" value={true}
                    checked={item.completed}
                    icon={<RadioButtonUncheckedIcon sx={{height:28, width:28}} />}
                    checkedIcon={<CheckCircleIcon sx={{height:28, width:28}} />}
                    disabled={Boolean(review.submittedOn)}
                    onClick={(event: any) => handleCheckItem(item, index, event.target.checked)}
          />
        </TableCell>
        <TableCell>
          {content}
        </TableCell>
        <TableCell>
          {button}
        </TableCell>
      </TableRow>
    )
  }

  const handleChangeView = (view: string) => {
    if (onChangeView) {
      onChangeView(view)
    }
  }

  const handlePrev = (event: any) => {
    if (onChangeView) {
      onChangeView("budget")
    }
  }

  const handleNext = (event: any) => {
    if (onChangeView) {
      onChangeView("timeline")
    }
  }

  const handleCancel = async (event: any) => {
    const input: UpdateReviewInput = {
      id: review.id,
      status: ReviewStatus.InProgress
    }

    const update = await userStore.updateReview(input)
    if (update && onChange) {
      onChange(update)
    }
    if (update && onClose) {
      onClose()
    }
  }

  const handleDecline = async (event: any) => {
    if (onDecline) {
      onDecline(review)
    }
  }

  const handleSubmit = async (event: any) => {
    const input: UpdateReviewInput = {
      id: review.id,
      submittedOn: getISODateToday(),
      status: ReviewStatus.Submitted
    }

    const update = await userStore.updateReview(input)
    if (update && onChange) {
      notify.show("success", "Thanks for completing and submitting the annual review!")
      onChange(update)
    }
    if (update && onSubmit) {
      onSubmit(update)
    }
  }

  // Determine if all items are completed
  const canSubmit = !review.submittedOn && review.status !== ReviewStatus.Declined &&
                              review.checklist.findIndex((item: ReviewChecklistItem) => !item.completed) < 0
  const canFinishLater = onClose && !review.submittedOn
  const canDecline = onDecline && !review.managed && !review.submittedOn
  const isAdvisorOrAdmin = userStore.isAdvisorOrAdmin

  let title = ""
  let description = ""
  if (!step || step === ReviewStep.Checklist) {
    title = "Annual Review Checklist"
    if (review.managed) {
      description = "We know your time is valuable so in order to be the most efficient with it, please complete the below items, preferably 3 business days prior to your appointment.\n" +
        "Check items as you complete them and click Submit after all items are checked."
    } else {
      description = "To start the year, please review and update your timeline, asset & liability balances and budget. This will create a beginning of year snapshot for accurate projections. Please enter balances and amounts for Jan 1st or as close as possible."
    }
  } else {
    title = "Complete Annual Review Checklist"
    if (canSubmit) {
      description = "Please click Submit to complete this review."
    } else {
      description = "Please complete the remaining steps and then click Submit."
    }
  }
  let status
  if (review.status === ReviewStatus.Submitted && review.submittedOn) {
    status = `${humanizeString(reviewStatus)} on ${isoToLocalDateString(review.submittedOn)}`
  } else {
    status = `${humanizeString(reviewStatus)} on ${isoToLocalDateString(review.updatedAt)}`
  }

  const height = isDialog ? "calc(100vh - 220px)" : "calc(100vh - 140px)"

  return (
    <React.Fragment>
    <DialogContent sx={{ my:0, p:0, height:height, maxHeight:height, overflow:"scroll"}}>
      <Box sx={{display:"flex", flexDirection:"column", alignItems:"flex-start", mt:1, pl:0, pb:0, mb:1}}>
        <Typography variant="h2" color="primary" gutterBottom>{title}</Typography>
        <Typography variant="body2" gutterBottom>
          {description}
        </Typography>
        {isAdvisorOrAdmin &&
          <Typography variant="body2" gutterBottom sx={{mt:1}}>
            Status: <b>{status}</b>
          </Typography>
        }
        {/*<Box display="flex" justifyContent="space-between" mt={1}>*/}
        {/*  <Typography variant="h5">Checklist</Typography>*/}
        {/*</Box>*/}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} align="center">Done</TableCell>
              <TableCell>Item</TableCell>
              <TableCell width={40}>Go To</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {review.checklist.map((item: ReviewChecklistItem, index: number) =>
              renderChecklistItem(item, index)
            )}
          </TableBody>
        </Table>
      </Box>
    </DialogContent>
    <DialogActions>
      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="center">
        <Box display="flex" justifyContent="flex-start">
          {canFinishLater &&
            <DialogButton variant="secondary" onClick={handleCancel}>
              Sign Out & Finish Later
            </DialogButton>
          }
          {canDecline &&
            <DialogButton variant="secondary" spacing="left" onClick={handleDecline}>
              Decline
            </DialogButton>
          }
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end">
        {step === ReviewStep.Complete &&
          <DialogButton variant="secondary" spacing="left" onClick={handlePrev}>
            Prev
          </DialogButton>
        }
        {step === ReviewStep.Checklist &&
          <DialogButton variant="primary" spacing="left" onClick={handleNext}>
            Next
          </DialogButton>
        }
        <DialogButton variant="primary" spacing="left" onClick={handleSubmit} disabled={!canSubmit}
                      customClassName={{...globalStyles.webFlowButton, fontWeight:700, width:100}}>
          {review.submittedOn ? "Submitted" : "Submit"}
        </DialogButton>
      </Box>
    </DialogActions>
    </React.Fragment>
  )
}

export default ReviewChecklist